import {
    Button,
    Card,
    Menu,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableRow,
    Typography
} from '@mui/material';

import { SearchGrid } from '@/components/SearchGrid';
import {
    FONT_WEIGHT,
    SMBold,
    SMRegular,
    PageHeading,
    PageSubHeading
} from '@/typography/Typography';
import { Dropdown, DropdownType } from '@/components/shared/Dropdown';
import { useEffect, useRef, useState } from 'react';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
    TableHeader,
    TableHeaderCellWithCheckbox,
    TableCellNoPadding,
    TableCell,
    TableSortLabel
} from '@/components/shared/styles/Table';
import {
    TablePagination,
    usePagination
} from '@/components/shared/TablePagination';
import {
    useGetAllBankTransactionReports,
    useGetHouseAccounts
} from '@/services/paymentsQuery';
import { PrimaryButton } from '@/components/shared/Button';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useTranslation } from 'react-i18next';
import Chip from '@/components/shared/Chip';
import CardHeader from '@/components/shared/CardHeader';
import { useAppDispatch } from '@/lib/hooks';
import { setLoader } from '@/lib/slices/globalLoaderSlice';
import IgnoreTransactionModal from './IgnoreTransactionModel';
import { BankTransaction } from '@/services/PaymentsServiceV2';
import CancelTransactionModel from './CancelTransactionModel';
import CreateTopUpModel from './CreateTopUpModel';
import { useFormatting } from '@/utils/formatting';
import { MenuItem } from '@/components/shared/Menu';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { TransactionMonitoringDetailDialog } from './TransactionMonitoringDetailDialog';
import { useTransactionMonitoringModalStore } from './TransactionMonitoringStore';
import BankConnectionStatus from './BankConnectionStatus';

// All available status values from BankTransactionRecordStatus enum
const ALL_STATUSES = [
    'ExistsInBankOnly',
    'ExistsInInstapaidOnly',
    'MappedAutomatically',
    'MappedManually',
    'Ignored',
    'TopUpCreated',
    'EmployeeTransactionCancelled',
    'AutoTopUpScheduled',
    'AutoTopUpConfirmed',
    'EmployeeTransactionRefunded',
    'MappedWithWarnings'
] as const;

type TransactionStatus = (typeof ALL_STATUSES)[number];
type TransactionFilterType = 'all' | TransactionStatus;

const STATUS_TO_CHIP_COLOR = {
    ExistsInBankOnly: 'error',
    ExistsInInstapaidOnly: 'warning',
    MappedAutomatically: 'success',
    MappedManually: 'success',
    Ignored: 'muted',
    TopUpCreated: 'success',
    EmployeeTransactionCancelled: 'muted',
    AutoTopUpScheduled: 'info',
    AutoTopUpConfirmed: 'success',
    EmployeeTransactionRefunded: 'success',
    MappedWithWarnings: 'warning'
};

const UI_STATE = {
    LIST: 'list',
    IGNORE_TRANSACTION: 'ignore_transaction',
    CANCEL_TRANSACTION: 'cancel_transaction',
    CREATE_TOPUP: 'create_topup'
};

const TransactionsMonitoringPage = () => {
    const { t } = useTranslation();

    const [uiState, setUiState] = useState(UI_STATE.LIST);
    const [selectedTransactionId, setSelectedTransactionId] = useState('');

    const openMonitoringModal = useTransactionMonitoringModalStore(
        (state) => state.openModal
    );

    // Create filter options: "All" + individual statuses sorted alphabetically
    const statusOptions = ALL_STATUSES.map((status) => ({
        value: status,
        label: t(
            `transaction_monitoring.status_${status
                .replace(/([A-Z])/g, '_$1')
                .toLowerCase()
                .slice(1)}`
        )
    })).sort((a, b) => a.label.localeCompare(b.label));

    const types = [
        {
            value: 'all',
            label: t('transaction_monitoring.show_all_transactions')
        },
        ...statusOptions
    ];

    const [transactionType, setTransactionType] =
        useState<TransactionFilterType>('all');

    const [selectedAccountId, setSelectedAccountId] = useState<string>('');

    const {
        data: houseAccounts,
        isLoading: isLoadingHouseAccounts,
        isError: isHouseAccountsError
    } = useGetHouseAccounts();

    // Defence in depth: the endpoint returns active accounts only today — keep
    // inactive accounts unselectable in case that ever changes.
    const accountChoices = (houseAccounts?.data ?? [])
        .filter((account) => account.isActive)
        .map((account) => ({
            value: account.instaPaidHouseAccountId,
            label: `${account.name} (${account.currency})`
        }));

    const hasAccounts = accountChoices.length > 0;

    const { page, resetPage, rowsPerPage, setRowsPerPage } = usePagination();
    const prevTransactionTypeRef = useRef(transactionType);

    // Prepare statuses array for API call
    const statusesFilter =
        transactionType === 'all'
            ? undefined // Don't filter by status, get all
            : [transactionType]; // Filter by selected status

    const { data, refetch, isLoading } = useGetAllBankTransactionReports({
        instaPaidHouseAccountId: selectedAccountId || undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        statuses: statusesFilter
    });

    useEffect(() => {
        if (prevTransactionTypeRef.current === transactionType) {
            return;
        }
        prevTransactionTypeRef.current = transactionType;
        resetPage();
    }, [transactionType]);

    const { syncSyncBankTransactionRecords } = usePaymentsMutations();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const [selectedTransaction, setSelectedTransaction] =
        useState<BankTransaction | null>(null);

    const handleMenuClick = (
        event: React.MouseEvent<HTMLButtonElement>,
        transaction: BankTransaction
    ) => {
        setAnchorEl(event.currentTarget);
        setSelectedTransaction(transaction);
        setSelectedTransactionId(transaction?.bankTransactionRecordId || '');
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        if (syncSyncBankTransactionRecords.isSuccess && selectedAccountId) {
            refetch();
        }
    }, [syncSyncBankTransactionRecords.isSuccess]);

    const dispatch = useAppDispatch();

    // isLoading is false while the query is disabled (no account selected), so
    // the global backdrop never traps the operator before they pick an account.
    useEffect(() => {
        dispatch(setLoader(isLoading));
    }, [isLoading]);

    const backToList = () => {
        setUiState(UI_STATE.LIST);
        setSelectedTransactionId('');
        setSelectedTransaction(null);
    };

    const { getBalanceLabel, formatDate } = useFormatting();

    const [dateSortDirection, setDateSortDirection] = useState<'desc' | 'asc'>(
        'desc'
    );

    const { setAlert } = useNotificationsSnackbar();

    useEffect(() => {
        if (syncSyncBankTransactionRecords.isSuccess) {
            setAlert(
                t('transaction_monitoring.sync_with_bank_success'),
                'success'
            );
        }
    }, [syncSyncBankTransactionRecords.isSuccess]);

    const actions = (
        <Stack direction="row" gap={'8px'} alignItems={'center'}>
            {selectedAccountId && (
                <BankConnectionStatus
                    instaPaidHouseAccountId={selectedAccountId}
                />
            )}
            <PrimaryButton
                onClick={() => {
                    syncSyncBankTransactionRecords.mutate();
                }}
                size="small"
            >
                {t('transaction_monitoring.sync_with_bank')}
            </PrimaryButton>
        </Stack>
    );

    const [sortedBankTransactions, setSortedBankTransactions] = useState<
        BankTransaction[]
    >([]);

    useEffect(() => {
        if (data?.data?.bankTransactions) {
            const sorted = [...data.data.bankTransactions].sort((a, b) => {
                if (dateSortDirection === 'asc') {
                    return (
                        a?.date?.toDate().getTime() -
                        b?.date?.toDate().getTime()
                    );
                } else {
                    return (
                        b?.date?.toDate().getTime() -
                        a?.date?.toDate().getTime()
                    );
                }
            });
            setSortedBankTransactions(sorted);
        }
    }, [dateSortDirection, data?.data?.bankTransactions]);

    return (
        <>
            <div>
                {/* House-account selector sits top-right of the page header. */}
                <Stack
                    direction={'row'}
                    justifyContent={'space-between'}
                    alignItems={'flex-start'}
                    gap={2}
                >
                    <div>
                        <PageHeading>
                            {t('transaction_monitoring.page_title')}
                        </PageHeading>
                        <PageSubHeading>
                            {t('transaction_monitoring.page_subtitle')}
                        </PageSubHeading>
                    </div>
                    <Stack
                        direction={'column'}
                        alignItems={'flex-end'}
                        gap={0.5}
                    >
                        <SMBold noWrap={true}>
                            {t('transaction_monitoring.select_house_account')}
                        </SMBold>
                        {isLoadingHouseAccounts ? (
                            <SMRegular>
                                {t(
                                    'transaction_monitoring.loading_house_accounts'
                                )}
                            </SMRegular>
                        ) : isHouseAccountsError ? (
                            <SMRegular>
                                {t(
                                    'transaction_monitoring.house_accounts_error'
                                )}
                            </SMRegular>
                        ) : !hasAccounts ? (
                            <SMRegular>
                                {t('transaction_monitoring.no_house_accounts')}
                            </SMRegular>
                        ) : (
                            <Dropdown
                                style={{ width: '400px' }}
                                choices={accountChoices}
                                label={t(
                                    'transaction_monitoring.select_house_account'
                                )}
                                value={selectedAccountId}
                                type={DropdownType.Single}
                                onChange={(val) => {
                                    setSelectedAccountId((val as string) || '');
                                    resetPage();
                                }}
                            />
                        )}
                    </Stack>
                </Stack>

                {selectedAccountId && (
                    <SearchGrid container justifyContent={'space-between'}>
                        <Stack
                            direction={'row'}
                            alignItems={'center'}
                            gap={1}
                            justifyContent={'space-between'}
                        >
                            <div>
                                <SMBold noWrap={true}>{t('filter_by')}</SMBold>
                            </div>
                            <Dropdown
                                style={{ width: '400px' }}
                                choices={types}
                                label=""
                                value={transactionType}
                                type={DropdownType.SingleShowTitle}
                                onChange={(val) => {
                                    setTransactionType(
                                        val as TransactionFilterType
                                    );
                                }}
                            />
                        </Stack>
                    </SearchGrid>
                )}

                {!selectedAccountId &&
                    !isLoadingHouseAccounts &&
                    !isHouseAccountsError &&
                    hasAccounts && (
                        <PageSubHeading>
                            {t('transaction_monitoring.choose_account_prompt')}
                        </PageSubHeading>
                    )}

                {selectedAccountId && (
                    <Card>
                        <CardHeader
                            label={
                                transactionType === 'all'
                                    ? t(
                                          'transaction_monitoring.all_statuses_label'
                                      )
                                    : t(
                                          `transaction_monitoring.status_${transactionType
                                              .replace(/([A-Z])/g, '_$1')
                                              .toLowerCase()
                                              .slice(1)}`
                                      )
                            }
                            chip={
                                <Chip
                                    icon={<span></span>}
                                    label={
                                        <Typography
                                            fontWeight={FONT_WEIGHT.SEMI_BOLD}
                                        >
                                            {data?.data?.total}
                                        </Typography>
                                    }
                                    color={'success'}
                                />
                            }
                            actions={actions}
                        />
                        <TableContainer style={{ minWidth: '1024px' }}>
                            <Table sx={{}} aria-label="simple table">
                                <TableHeader>
                                    <TableRow>
                                        <TableHeaderCellWithCheckbox
                                            style={{ paddingLeft: 24 }}
                                        >
                                            <TableSortLabel
                                                direction={dateSortDirection}
                                                onClick={() =>
                                                    setDateSortDirection(
                                                        dateSortDirection ===
                                                            'asc'
                                                            ? 'desc'
                                                            : 'asc'
                                                    )
                                                }
                                            >
                                                {t(
                                                    'transaction_monitoring.date_time'
                                                )}
                                            </TableSortLabel>
                                        </TableHeaderCellWithCheckbox>
                                        <TableHeaderCellWithCheckbox
                                            align="left"
                                            style={{ paddingLeft: 0 }}
                                        >
                                            {t('transaction_monitoring.from')}
                                        </TableHeaderCellWithCheckbox>
                                        <TableHeaderCellWithCheckbox
                                            align="left"
                                            style={{ paddingLeft: 0 }}
                                        >
                                            {t(
                                                'transaction_monitoring.comment'
                                            )}
                                        </TableHeaderCellWithCheckbox>
                                        <TableHeaderCellWithCheckbox
                                            align="left"
                                            style={{ paddingLeft: 0 }}
                                        >
                                            {t('transaction_monitoring.text')}
                                        </TableHeaderCellWithCheckbox>
                                        <TableHeaderCellWithCheckbox
                                            style={{ paddingLeft: 0 }}
                                            align="left"
                                        >
                                            {t(
                                                'transaction_monitoring.end_to_end_id'
                                            )}
                                        </TableHeaderCellWithCheckbox>
                                        <TableHeaderCellWithCheckbox
                                            style={{
                                                paddingLeft: 0
                                            }}
                                            align="left"
                                        >
                                            {t('transaction_monitoring.status')}
                                        </TableHeaderCellWithCheckbox>

                                        <TableHeaderCellWithCheckbox
                                            style={{
                                                paddingRight: 0
                                            }}
                                            align="right"
                                        >
                                            {t('transaction_monitoring.amount')}
                                        </TableHeaderCellWithCheckbox>

                                        <TableHeaderCellWithCheckbox></TableHeaderCellWithCheckbox>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sortedBankTransactions?.map(
                                        (transaction) => {
                                            return (
                                                <TableRow
                                                    key={
                                                        transaction?.bankTransactionRecordId
                                                    }
                                                    onClick={() => {
                                                        if (
                                                            transaction?.bankTransactionRecordId
                                                        ) {
                                                            openMonitoringModal(
                                                                transaction.bankTransactionRecordId
                                                            );
                                                        }
                                                    }}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            backgroundColor:
                                                                'rgba(0, 0, 0, 0.04)'
                                                        }
                                                    }}
                                                >
                                                    <TableCell>
                                                        {formatDate(
                                                            transaction?.date,
                                                            'DD. MMM HH:mm'
                                                        )}
                                                    </TableCell>
                                                    <TableCellNoPadding>
                                                        {transaction?.from}
                                                    </TableCellNoPadding>
                                                    <TableCellNoPadding>
                                                        {transaction?.comment}
                                                    </TableCellNoPadding>
                                                    <TableCellNoPadding>
                                                        {transaction?.text}
                                                    </TableCellNoPadding>

                                                    <TableCellNoPadding>
                                                        {
                                                            transaction?.bankTransactionRecordId
                                                        }
                                                    </TableCellNoPadding>

                                                    <TableCellNoPadding>
                                                        <Chip
                                                            color={
                                                                STATUS_TO_CHIP_COLOR[
                                                                    transaction.status as keyof typeof STATUS_TO_CHIP_COLOR
                                                                ] as any
                                                            }
                                                            label={
                                                                transaction.status
                                                            }
                                                        />
                                                    </TableCellNoPadding>
                                                    <TableCellNoPadding align="right">
                                                        {getBalanceLabel(
                                                            transaction.amount,
                                                            transaction.currency,
                                                            undefined,
                                                            true
                                                        )}
                                                    </TableCellNoPadding>
                                                    <TableCellNoPadding align="right">
                                                        <Button
                                                            color="secondary"
                                                            aria-controls={
                                                                open
                                                                    ? 'basic-menu'
                                                                    : undefined
                                                            }
                                                            aria-haspopup="true"
                                                            aria-expanded={
                                                                open
                                                                    ? 'true'
                                                                    : undefined
                                                            }
                                                            onClick={(
                                                                event
                                                            ) => {
                                                                event.stopPropagation();
                                                                handleMenuClick(
                                                                    event,
                                                                    transaction
                                                                );
                                                            }}
                                                        >
                                                            <MoreVertIcon />
                                                        </Button>
                                                    </TableCellNoPadding>
                                                </TableRow>
                                            );
                                        }
                                    )}
                                </TableBody>
                            </Table>

                            <Menu
                                id="basic-menu"
                                MenuListProps={{
                                    'aria-labelledby': 'basic-button'
                                }}
                                anchorEl={anchorEl}
                                sx={{ boxShadow: 'none' }}
                                open={open}
                                onClose={handleMenuClose}
                            >
                                <MenuItem
                                    onClick={() => {
                                        setUiState(UI_STATE.IGNORE_TRANSACTION);
                                        handleMenuClose();
                                    }}
                                >
                                    Ignore transaction
                                </MenuItem>
                                {selectedTransaction?.status ==
                                    'ExistsInBankOnly' && (
                                    <>
                                        <MenuItem
                                            onClick={() => {
                                                setUiState(
                                                    UI_STATE.CREATE_TOPUP
                                                );
                                                handleMenuClose();
                                            }}
                                        >
                                            Create Top-up
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() => {
                                                setUiState(
                                                    UI_STATE.CANCEL_TRANSACTION
                                                );
                                                handleMenuClose();
                                            }}
                                        >
                                            Cancel a transaction
                                        </MenuItem>
                                    </>
                                )}
                            </Menu>
                        </TableContainer>
                        <TablePagination
                            count={data?.data?.total || 0}
                            page={page}
                            onPageChange={() => {}}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(val) => {
                                setRowsPerPage(val as number);
                            }}
                            rowsPerPageOptions={[
                                {
                                    label: '16',
                                    value: 16
                                },
                                {
                                    label: '24',
                                    value: 24
                                },
                                {
                                    label: '32',
                                    value: 32
                                },
                                {
                                    label: 'All',
                                    value: -1
                                }
                            ]}
                        />
                    </Card>
                )}
            </div>

            <IgnoreTransactionModal
                open={uiState == UI_STATE.IGNORE_TRANSACTION}
                selectedTransactionId={selectedTransactionId}
                handleClose={backToList}
            />
            <CancelTransactionModel
                open={uiState == UI_STATE.CANCEL_TRANSACTION}
                selectedTransaction={selectedTransaction}
                handleClose={backToList}
            />
            <CreateTopUpModel
                selectedTransaction={selectedTransaction}
                open={uiState == UI_STATE.CREATE_TOPUP}
                handleClose={backToList}
            />
            <TransactionMonitoringDetailDialog />
        </>
    );
};

export default TransactionsMonitoringPage;
